import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import IndeterminateCheckBoxOutlinedIcon from "@mui/icons-material/IndeterminateCheckBoxOutlined";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ActionMenu from "@/components/ActionMenu";
import { useUserStore } from "@/hooks/useUserStore";
import { Theme } from "@mui/material";
import { PermissionDeniedModal } from "@/components/PermissionDeniedModal/PermissionDeniedModal";
import { AlertDialog } from "@/components/AlertDialog/AlertDialog";
import { EnrollmentStatus, Enrollment } from "@/domain/enrollment.interface";
import { useProgramBudget } from "@/hooks/usePrograms";

interface EnrollmentMenuProps {
  theme: Theme;
  handleEditEnrollment: () => void;
  handleDisenroll: () => void;
  handleOnDemandIncentive: () => void;
  disabled?: boolean;
  enrollmentStatus: EnrollmentStatus;
  enrollment?: Enrollment;
}

const EnrollmentMenu: React.FC<EnrollmentMenuProps> = ({
  handleEditEnrollment,
  handleDisenroll,
  handleOnDemandIncentive,
  disabled,
  theme,
  enrollmentStatus,
  enrollment,
}) => {
  const { t } = useTranslation("clients");
  const { userHasAccessToCode } = useUserStore();
  const [showPermissionDeniedModal, setShowPermissionDeniedModal] =
    useState(false);
  const [showCompletedMessage, setShowCompletedMessage] = useState(false);

  // Fetch program budget data
  const programId = enrollment?.programId ?? enrollment?.program?._id ?? "";
  const { budgetData } = useProgramBudget(programId, !!enrollment);

  const handlePermissionAlert = () => {
    setShowPermissionDeniedModal(true);
  };

  const handleDisenrollClick = () => {
    // If enrollment is completed, show message
    if (enrollmentStatus === EnrollmentStatus.COMPLETED) {
      setShowCompletedMessage(true);
      return;
    }

    // If enrollment is active and user has permission, handle disenroll
    if (enrollmentStatus === EnrollmentStatus.ACTIVE) {
      if (userHasAccessToCode("PARTICIPANT_DISENROLL_CLIENT")) {
        handleDisenroll();
      } else {
        handlePermissionAlert();
      }
    }
  };

  const isActive = enrollmentStatus === EnrollmentStatus.ACTIVE;
  const isCompleted = enrollmentStatus === EnrollmentStatus.COMPLETED;
  const canShowOnDemandIncentive = isActive || isCompleted;
  const isDisenrolled = enrollmentStatus === EnrollmentStatus.DISENROLLED;

  const isOnDemandDisabled = useMemo(() => {
    if (!enrollment) return true;
    const hasLimits =
      enrollment?.program?.parameters?.minAmount &&
      enrollment?.program?.parameters?.maxAmount;
    const isDisabledByConfig =
      !hasLimits || !enrollment?.program?.parameters?.enableOnDemandIncentive;

    // Check if program has budget limit and insufficient funds
    // Note: We disable if remaining budget is less than minAmount, which means
    // the user cannot create even the minimum incentive. This allows the button
    // to remain enabled if there's enough budget for at least the minimum amount,
    // even if it's less than the maximum. The modal will handle validation for
    // amounts exceeding the remaining budget.
    const hasInsufficientBudget =
      budgetData?.hasBudgetLimit &&
      budgetData?.remainingBudget !== null &&
      budgetData?.remainingBudget !== undefined &&
      (budgetData.remainingBudget <= 0 ||
        budgetData.remainingBudget <
          (enrollment?.program?.parameters?.minAmount ?? 0));

    return isDisabledByConfig || hasInsufficientBudget;
  }, [enrollment, budgetData]);

  const actionMenuOptions = [
    // Edit option - only show if enrollment is ACTIVE
    ...(isActive
      ? [
          {
            icon: (
              <EditOutlinedIcon sx={{ color: "#6A6F81", fontSize: "16px" }} />
            ),
            value: t("client_profile.edit_active_enrollment"),
            onclick: () => {},
            onClick: userHasAccessToCode("PARTICIPANT_EDIT_ENROLLMENT")
              ? handleEditEnrollment
              : handlePermissionAlert,
          },
        ]
      : []),
    // On-demand incentive option - show for ACTIVE and COMPLETED enrollments
    ...(canShowOnDemandIncentive
      ? [
          {
            icon: (
              <AttachMoneyIcon sx={{ color: "#6A6F81", fontSize: "16px" }} />
            ),
            value: t("client_profile.on_demand_incentive"),
            onClick: userHasAccessToCode("PARTICIPANT_REWARD_MANUAL_ENTER")
              ? handleOnDemandIncentive
              : handlePermissionAlert,
            disabled: isOnDemandDisabled,
          },
        ]
      : []),
    // Disenroll option - disabled only if DISENROLLED, enabled for ACTIVE and COMPLETED
    {
      icon: <IndeterminateCheckBoxOutlinedIcon sx={{ fontSize: "16px" }} />,
      value: t("client_profile.disenroll"),
      onclick: () => {},
      onClick: handleDisenrollClick,
      disabled: isDisenrolled,
      sx: {
        color: theme.palette.error.main,
      },
    },
  ];

  return (
    <>
      <ActionMenu
        options={actionMenuOptions}
        id="profile-menu"
        data-testid="profile-menu"
        isSquare
        squareSx={{
          color: theme.palette.icon.dark,
          backgroundColor: "#ffffff",
          "&:hover": {
            backgroundColor: theme.palette.surface.main,
          },
          borderRadius: "8px",
          width: "40px",
          height: "40px",
          border: `1px solid ${theme.palette.primary.dark}`,
        }}
        disabled={disabled}
      />

      <PermissionDeniedModal
        open={showPermissionDeniedModal}
        onClose={() => setShowPermissionDeniedModal(false)}
      />

      <AlertDialog
        open={showCompletedMessage}
        title="Cannot Disenroll"
        description="A participant that has completed the program cannot be disenrolled."
        confirmText="Close"
        onConfirm={() => setShowCompletedMessage(false)}
        onCancel={() => setShowCompletedMessage(false)}
      />
    </>
  );
};

export default EnrollmentMenu;
